export const studentGuideContent = `# Praxis — Student Guide

Welcome to Praxis, your guided workshop platform. This guide walks you through everything you need to know to get started and make the most of your workshop experience.

---

## 1. Logging In

Once your instructor enrolls you in a session, you'll receive an email with your login credentials (email and password) and a link to the Praxis platform.

1. Open the Praxis URL from your email
2. Enter the **email** and **password** provided in the email
3. Click **Login**

> If you see an error, double-check your credentials. If the problem persists, contact your instructor.

---

## 2. Your Dashboard

After logging in, you'll land on your **Student Dashboard**. This is your home base.

### What You'll See

- A **welcome message** with your name
- **Session cards** for every workshop you're enrolled in

### Understanding Session Cards

Each card represents a workshop session and shows:

| Element | What It Means |
|---------|---------------|
| **Workshop title** | The name of the workshop |
| **Description** | A brief summary of what the workshop covers |
| **Difficulty badge** | Beginner (green), Intermediate (orange), or Advanced (red) |
| **Step count & duration** | How many steps and estimated total time |
| **Status chip** | Your current state in this session (see below) |

### Session Statuses

| Status | Meaning | Action Available |
|--------|---------|-----------------|
| **Upcoming** | Session is scheduled but hasn't started | Wait for your instructor to start it |
| **Not Started** | Session is active, you haven't begun | Click **Start Tech Eval** |
| **In Progress** | You've started but haven't finished | Click **Continue** |
| **Completed** | You've finished all steps | Click **Review** to revisit |
| **Session Ended** | The instructor has closed the session | Click **View Results** |

### No Sessions?

If your dashboard is empty, your instructor hasn't added you to a session yet. Check back later or reach out to them.

---

## 3. The Workshop View

When you enter a session, you'll see a **split-screen layout** with two main areas:

\`\`\`
+---------------------------+-----------------------------+
|                           |                             |
|   Instructions Panel      |   Environment Panel         |
|   (Left Side)             |   (Right Side)              |
|                           |                             |
|   Step content, guides,   |   Remote Desktop or         |
|   checklists, images      |   Web Application           |
|                           |                             |
+---------------------------+-----------------------------+
\`\`\`

### Header Bar

At the top of the screen you'll find:

- **Back button** — returns to your dashboard
- **Workshop title**
- **Step counter** — "Step X of Y"
- **Progress bar** — your overall completion percentage
- **Connection status** — Live (green), Connecting (orange), or Offline (grey)
- **Request Help button** — reach out to your instructor at any time

---

## 4. Navigating Steps

### The Step List

Click the **step header** in the left panel to expand the full step list. You'll see all steps with their status:

| Icon | Meaning |
|------|---------|
| Filled circle | Current step |
| Checkmark | Completed step |
| Empty circle | Not yet started |
| Lock icon | Locked — complete previous steps first |

Click any unlocked step to jump to it. The list closes automatically after you select a step.

### Step Types

Each step has a type that determines what you'll see on the right side:

- **Introduction** — Instructions only, no environment panel
- **Remote Desktop** — A live remote desktop session alongside your instructions
- **Web Application** — A web app loaded in an iframe alongside your instructions

### Moving Between Steps

- Use the **Previous** and **Next** buttons at the bottom of the instructions panel
- Or click directly on a step in the step list
- Progress dots at the top give you a quick visual of where you are

### Locked Steps

If your instructor has enabled step ordering, you must complete steps in sequence. Locked steps appear greyed out with a lock icon. Complete the current step to unlock the next one.

---

## 5. Working with Environments

### Your Credentials

For steps that involve a remote desktop or web application, you'll see a collapsible **My Environment** panel near the top of the instructions. It contains your login credentials and URLs:

- **Tenant URL** — the address of the system you'll be working in
- **Admin Email** — your login email for the tenant
- **Admin Password** — your login password (hidden by default)
- **Skytap Portal URL** — link to your virtual environment
- **Portal Password** — password for the portal (hidden by default)

#### Revealing Passwords

Passwords are hidden by default. Click the **eye icon** next to any password field to reveal it.

#### Copying Values

Click the **copy icon** next to any credential field to copy it to your clipboard. You'll see a brief confirmation when the copy succeeds.

### Remote Desktop

The right panel shows your remote desktop session. You can interact with it just like a regular computer — click, type, navigate.

#### Copy & Paste with Remote Desktop

Since the remote desktop runs in your browser, clipboard sharing works a little differently:

**To paste text INTO the remote desktop:**

1. Click the **Paste** button in the environment header
2. Type or paste (Ctrl+V) your text into the dialog
3. Click **Send to Remote**
4. The text will be typed into the remote session at the cursor position

**To copy text FROM the remote desktop:**

1. Select the text inside the remote desktop and press Ctrl+C
2. Click the **Copy** button in the environment header
3. The copied text appears in the dialog
4. Click **Copy to Clipboard** to save it locally

### Web Applications

For web app steps, the target application loads directly in the right panel. You can interact with it normally. If the URL isn't configured, you'll see a message asking you to contact your instructor.

---

## 6. Completing Steps

How you complete a step depends on its type:

### Manual Completion

Most steps require you to click **Mark Complete & Continue** when you're done. This marks the step as complete and advances you to the next one.

### Checklist-Gated Steps

Some steps include a **checklist** of tasks you need to complete. The completion button stays disabled until you've checked off every item.

- Check each item as you complete the task
- Checked items show a strikethrough
- A counter below shows "X of Y tasks complete"
- Once all items are checked, the **Mark Complete & Continue** button activates with a pulsing animation

### Auto-Advance Steps

Some steps advance automatically when their criteria are met. You'll see a message: *"This step will auto-advance when tasks are complete."* No button click needed.

### Completion Screen

After completing all steps, you'll see a congratulations screen with your stats:

- **Steps Completed** — total count
- **Completion Time** — how long it took
- **Help Requests** — how many times you asked for help

Click **Back to Dashboard** to return to your session list.

---

## 7. Requesting Help

You can ask your instructor for help at any point during the workshop.

### Sending a Help Request

1. Click the **Request Help** button in the header bar
2. A dialog opens showing your current context (which step you're on, your progress)
3. Optionally set the priority to **Urgent** if you're blocked
4. Describe your issue in the message box (up to 500 characters)
5. Click **Send Help Request**

Your instructor will be notified immediately.

### The Help Conversation

After sending a request, a **conversation drawer** opens on the right side. This is a live chat between you and your instructor:

- Type your message and press **Enter** to send
- Press **Shift+Enter** to add a new line without sending
- Messages appear in real-time — no need to refresh
- Your messages appear on the right, instructor messages on the left

### Reacting to Messages

Hover over any message to see a reaction button. Click it to choose from 8 emoji reactions. Click a reaction again to remove it.

### Resolving Your Help Request

Once your issue is resolved:

1. Click the **Resolve** button at the top of the conversation drawer
2. Confirm in the dialog that appears
3. Your instructor will be notified that you no longer need help

Your instructor can also resolve the request from their end. You'll see a notification when this happens.

### Help Request Indicator

If you close the conversation drawer while a help request is active, a floating **Help Request Active** indicator appears in the bottom-right corner. Click it to reopen the conversation. A badge shows the number of unread messages.

---

## 8. Emoji Reactions

At the bottom of the instructions panel (above the action buttons), you'll find an **emoji reaction bar** with 5 options:

| Emoji | Meaning |
|-------|---------|
| Thumbs Up | I'm following along fine |
| Party Popper | That was great! |
| Heart | Love this content |
| Thinking Face | I'm a bit confused |
| Raised Hand | I have a question |

Click any emoji to send a real-time reaction to your instructor. There's a 3-second cooldown between reactions to prevent spam.

---

## 9. Tips & Shortcuts

### Keyboard Shortcuts

| Shortcut | Action |
|----------|--------|
| **Ctrl+B** | Toggle instructions panel collapse/expand |
| **Enter** | Send a help message |
| **Shift+Enter** | New line in help message |
| **Arrow keys** | Navigate images in the gallery lightbox |

### Resizing Panels

- **Drag the divider** between the instructions and environment panels to adjust their width
- **Double-click the divider** to reset to the default split (35/65)
- Your preferred size is remembered between sessions

### Collapsing Instructions

Click the **collapse arrow** at the top of the instructions panel (or press **Ctrl+B**) to minimize it to a thin rail. This gives the environment panel nearly full screen. The rail still shows numbered step circles you can click to navigate.

### Image Gallery

Click any image in the instructions to open it in a **lightbox** for a closer look. If there are multiple images on the step, use the **arrow keys** or on-screen arrows to browse through them.

### Copying Code Blocks

Code snippets in the instructions have a **Copy** button in the top-right corner. Click it to copy the code to your clipboard.

---

## 10. Troubleshooting

### "Session Hasn't Started Yet"

Your instructor hasn't started the session. Wait for them to begin, then refresh the page or return from your dashboard.

### Remote Desktop Shows "Not Available"

Your remote desktop hasn't been assigned yet. Contact your instructor — they may need to provision your environment.

### Connection Status Shows "Offline"

Your browser lost the real-time connection. This usually resolves on its own. If it persists:

1. Check your internet connection
2. Refresh the page
3. If the problem continues, contact your instructor

### Steps Are Locked

Your instructor has enabled sequential step ordering. Complete the current step before moving to the next one. If you believe you've completed it but it's still locked, try clicking **Mark Complete & Continue**.

### Checklist Items Won't Save

If checking a checklist item fails, you'll see a brief error. The checkbox will revert. Try again — it's usually a temporary network issue.

### Help Request Button Missing

Your instructor may have disabled help requests for this session. Reach out to them directly if you need assistance.

### Copy/Paste Not Working with Remote Desktop

Use the dedicated **Copy** and **Paste** buttons in the environment header instead of Ctrl+C/Ctrl+V. The remote desktop runs in an isolated session, so standard clipboard shortcuts don't cross the boundary.

---

**Need more help?** Use the **Request Help** button during your session, or contact your instructor directly.
`;
